import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, MessageCircle, Pen } from 'lucide-react';
import { clsx } from 'clsx';
import { readWorkbookJson, writeWorkbookJson, type WorkbookActivityCompletion } from './workbookPersist';

export type ConvertExample = { spoken: string; written: string };

export type ConvertItemView =
  | {
      q_no: number;
      kind: 'single';
      incomplete: boolean;
      spoken: string;
      expectedWritten?: string;
    }
  | {
      q_no: number;
      kind: 'multi';
      incomplete: boolean;
      spokenLines: string[];
      expectedWrittenLines: string[];
    };

export interface ConvertWordsCardProps {
  workbookLabel?: string;
  title: string;
  instructions?: string;
  sourcePage?: number;
  example: ConvertExample;
  items: ConvertItemView[];
  persistenceId?: string;
  onInteracted?: () => void;
  onWorkbookStatusChange?: (status: WorkbookActivityCompletion) => void;
}

function norm(s: string): string {
  return s.trim().replace(/\s+/g, ' ');
}

type PersistShape = {
  single: Record<number, string>;
  multi: Record<number, string[]>;
  step: number;
  checked: boolean;
};

export const ConvertWordsCard: React.FC<ConvertWordsCardProps> = ({
  workbookLabel,
  title,
  instructions,
  sourcePage,
  example,
  items,
  persistenceId,
  onInteracted,
  onWorkbookStatusChange,
}) => {
  const storageKey = persistenceId ? `g3-cv-${persistenceId}` : '';

  const hasAnswerKey = useMemo(
    () =>
      items.some((it) => {
        if (it.incomplete) return false;
        if (it.kind === 'single') return !!it.expectedWritten?.trim();
        return it.expectedWrittenLines.length > 0 && it.expectedWrittenLines.every((x) => x.trim());
      }),
    [items]
  );

  const initialSingle = useMemo(() => {
    const m: Record<number, string> = {};
    for (const it of items) {
      if (it.kind === 'single') m[it.q_no] = '';
    }
    return m;
  }, [items]);

  const initialMulti = useMemo(() => {
    const m: Record<number, string[]> = {};
    for (const it of items) {
      if (it.kind === 'multi') m[it.q_no] = it.spokenLines.map(() => '');
    }
    return m;
  }, [items]);

  const itemsRef = useRef(items);
  itemsRef.current = items;
  const itemsStableKey = items
    .map((it) =>
      it.kind === 'single'
        ? `s:${it.q_no}:${it.spoken}`
        : `m:${it.q_no}:${it.spokenLines.join('\u0002')}`
    )
    .join('\u0003');

  const [singleAnswers, setSingleAnswers] = useState(initialSingle);
  const [multiAnswers, setMultiAnswers] = useState(initialMulti);
  const [step, setStep] = useState(0);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    const list = itemsRef.current;
    const emptySingle: Record<number, string> = {};
    const emptyMulti: Record<number, string[]> = {};
    for (const it of list) {
      if (it.kind === 'single') emptySingle[it.q_no] = '';
      else if (it.kind === 'multi') emptyMulti[it.q_no] = it.spokenLines.map(() => '');
    }

    if (!storageKey) {
      setSingleAnswers(emptySingle);
      setMultiAnswers(emptyMulti);
      setStep(0);
      setChecked(false);
      return;
    }
    const loaded = readWorkbookJson<Partial<PersistShape> | null>(storageKey, null);
    if (loaded?.single && typeof loaded.single === 'object') {
      const merged = { ...emptySingle };
      const rawS = loaded.single as Record<string, unknown>;
      for (const k of Object.keys(rawS)) {
        const q = Number(k);
        if (!Number.isFinite(q)) continue;
        merged[q] = String(rawS[k] ?? '');
      }
      setSingleAnswers(merged);
    } else setSingleAnswers(emptySingle);
    if (loaded?.multi && typeof loaded.multi === 'object') {
      const merged = { ...emptyMulti };
      const rawM = loaded.multi as Record<string, unknown>;
      for (const k of Object.keys(rawM)) {
        const q = Number(k);
        if (!Number.isFinite(q)) continue;
        const row = rawM[k];
        if (Array.isArray(row) && merged[q]?.length === row.length) merged[q] = row.map(String);
      }
      setMultiAnswers(merged);
    } else setMultiAnswers(emptyMulti);
    const max = Math.max(0, list.length - 1);
    setStep(typeof loaded?.step === 'number' ? Math.min(Math.max(0, loaded.step), max) : 0);
    setChecked(false);
  }, [storageKey, itemsStableKey, items.length]);

  useEffect(() => {
    if (!storageKey) return;
    writeWorkbookJson(storageKey, { single: singleAnswers, multi: multiAnswers, step, checked } satisfies PersistShape);
  }, [storageKey, singleAnswers, multiAnswers, step, checked]);

  const mark = useCallback(() => onInteracted?.(), [onInteracted]);

  const it = items[Math.min(step, Math.max(0, items.length - 1))];

  const resetCurrent = () => {
    if (!it) return;
    mark();
    setChecked(false);
    if (it.kind === 'single') setSingleAnswers((p) => ({ ...p, [it.q_no]: '' }));
    else setMultiAnswers((p) => ({ ...p, [it.q_no]: it.spokenLines.map(() => '') }));
  };

  const resetAll = () => {
    mark();
    setSingleAnswers({ ...initialSingle });
    const mu: Record<number, string[]> = {};
    for (const x of items) {
      if (x.kind === 'multi') mu[x.q_no] = x.spokenLines.map(() => '');
    }
    setMultiAnswers(mu);
    setStep(0);
    setChecked(false);
  };

  const resultFor = (x: ConvertItemView) => {
    if (x.incomplete) return { skip: true as const };
    if (x.kind === 'single') {
      const exp = x.expectedWritten?.trim();
      if (!exp) return { skip: true as const };
      const ok = norm(singleAnswers[x.q_no] ?? '') === norm(exp);
      return { skip: false as const, ok };
    }
    const lines = x.expectedWrittenLines;
    if (!lines.length || !lines.every((w) => w.trim())) return { skip: true as const };
    const got = multiAnswers[x.q_no] ?? [];
    const ok = lines.every((exp, i) => norm(got[i] ?? '') === norm(exp));
    return { skip: false as const, ok };
  };

  const currentResult = it ? resultFor(it) : null;

  useEffect(() => {
    if (!onWorkbookStatusChange || items.length === 0) return;
    const fillable = items.filter((x) => !x.incomplete);
    if (fillable.length === 0) {
      onWorkbookStatusChange('completed');
      return;
    }
    const allFilled = fillable.every((x) => {
      if (x.kind === 'single') return (singleAnswers[x.q_no] ?? '').trim().length > 0;
      const arr = multiAnswers[x.q_no] ?? [];
      return x.spokenLines.every((_, i) => (arr[i] ?? '').trim().length > 0);
    });
    if (!allFilled) {
      const any = fillable.some((x) => {
        if (x.kind === 'single') return (singleAnswers[x.q_no] ?? '').trim().length > 0;
        const arr = multiAnswers[x.q_no] ?? [];
        return arr.some((v) => (v ?? '').trim().length > 0);
      });
      onWorkbookStatusChange(any ? 'in_progress' : 'not_started');
      return;
    }
    const allCorrect = fillable.every((x) => {
      if (x.incomplete) return true;
      if (x.kind === 'single') {
        const exp = x.expectedWritten?.trim();
        if (!exp) return true;
        return norm(singleAnswers[x.q_no] ?? '') === norm(exp);
      }
      const lines = x.expectedWrittenLines;
      if (!lines.length || !lines.every((w) => w.trim())) return true;
      const got = multiAnswers[x.q_no] ?? [];
      return lines.every((exp, i) => norm(got[i] ?? '') === norm(exp));
    });
    onWorkbookStatusChange(allCorrect ? 'completed' : 'in_progress');
  }, [onWorkbookStatusChange, items, singleAnswers, multiAnswers]);

  return (
    <div className="overflow-hidden rounded-[2rem] border-4 border-emerald-300/90 bg-gradient-to-br from-emerald-50 via-lime-50 to-green-50 shadow-[0_20px_50px_-12px_rgba(16,185,129,0.35)] dark:border-emerald-800/60 dark:from-emerald-950/40 dark:via-slate-900 dark:to-slate-900">
      <div className="border-b border-emerald-200/70 bg-white/55 px-5 py-4 backdrop-blur-sm dark:border-emerald-800/40 dark:bg-slate-900/50">
        {workbookLabel ? (
          <p className="text-sm font-extrabold text-emerald-800 tamil-text dark:text-emerald-200" lang="ta">
            {workbookLabel}
          </p>
        ) : null}
        <h2 className="mt-1 text-2xl font-extrabold text-emerald-950 tamil-text md:text-3xl dark:text-emerald-50" lang="ta">
          {title}
        </h2>
        {instructions ? (
          <p className="mt-2 text-lg leading-relaxed text-emerald-900/95 tamil-text dark:text-emerald-100/90" lang="ta">
            {instructions}
          </p>
        ) : null}
        {sourcePage != null ? (
          <span className="mt-3 inline-block rounded-full bg-emerald-600 px-3 py-1 text-sm font-bold text-white">p.{sourcePage}</span>
        ) : null}
      </div>

      <div className="px-4 py-6 sm:px-8">
        <div className="mb-6 grid gap-4 rounded-3xl border-4 border-white bg-gradient-to-r from-teal-400 to-emerald-500 p-5 text-white shadow-lg sm:grid-cols-2">
          <div>
            <p className="flex items-center gap-2 text-sm font-extrabold uppercase tracking-wide tamil-text opacity-90" lang="ta">
              <MessageCircle className="h-5 w-5" aria-hidden />
              பேச்சு
            </p>
            <p className="mt-2 text-2xl font-black leading-snug tamil-text" lang="ta">
              {example.spoken}
            </p>
          </div>
          <div>
            <p className="flex items-center gap-2 text-sm font-extrabold uppercase tracking-wide tamil-text opacity-90" lang="ta">
              <Pen className="h-5 w-5" aria-hidden />
              எழுத்து
            </p>
            <p className="mt-2 text-2xl font-black leading-snug tamil-text" lang="ta">
              {example.written}
            </p>
          </div>
        </div>

        {it ? (
          <>
            <div className="mb-4 flex justify-center gap-2">
              {items.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => {
                    mark();
                    setStep(i);
                    setChecked(false);
                  }}
                  className={clsx(
                    'h-3 w-3 rounded-full border-2 sm:h-4 sm:w-4',
                    i === step ? 'scale-125 border-emerald-600 bg-emerald-500' : 'border-emerald-300 bg-white dark:border-emerald-700 dark:bg-slate-700'
                  )}
                  aria-label={`வினா ${i + 1}`}
                />
              ))}
            </div>

            <div className="rounded-3xl border-4 border-white bg-white/95 p-6 shadow-inner dark:border-slate-600 dark:bg-slate-800/95">
              <p className="text-center text-sm font-extrabold text-emerald-800 tamil-text dark:text-emerald-200" lang="ta">
                பயிற்சி {step + 1} / {items.length}
              </p>

              {it.incomplete ? (
                <p className="mt-6 rounded-2xl border-4 border-amber-200 bg-amber-50 p-6 text-center text-lg font-bold text-amber-950 tamil-text dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-50" lang="ta">
                  இந்த பகுதி இன்னும் பரிசீலனையில் உள்ளது
                </p>
              ) : it.kind === 'single' ? (
                <div className="mt-6 space-y-4">
                  <div className="rounded-2xl border-4 border-emerald-200 bg-emerald-50/80 p-5 text-center dark:border-emerald-800 dark:bg-emerald-950/30">
                    <p className="text-sm font-extrabold text-emerald-800 tamil-text dark:text-emerald-200" lang="ta">
                      பேச்சு வடிவம்
                    </p>
                    <p className="mt-2 text-2xl font-black text-emerald-950 tamil-text dark:text-emerald-50" lang="ta">
                      {it.spoken}
                    </p>
                  </div>
                  <label className="block text-center text-lg font-extrabold text-emerald-900 tamil-text dark:text-emerald-100" lang="ta">
                    எழுத்து வடிவம் எழுதுக
                  </label>
                  <input
                    type="text"
                    value={singleAnswers[it.q_no] ?? ''}
                    onChange={(e) => {
                      mark();
                      setChecked(false);
                      setSingleAnswers((p) => ({ ...p, [it.q_no]: e.target.value }));
                    }}
                    className="w-full min-h-[56px] rounded-2xl border-4 border-emerald-300 bg-emerald-50/50 px-4 text-center text-2xl font-bold text-emerald-950 outline-none focus:border-amber-400 dark:border-emerald-700 dark:bg-slate-900 dark:text-white tamil-text"
                    lang="ta"
                    placeholder="…"
                  />
                </div>
              ) : (
                <div className="mt-6 space-y-5">
                  {it.spokenLines.map((line, li) => (
                    <div key={li} className="space-y-2">
                      <div className="rounded-2xl border-4 border-emerald-200 bg-emerald-50/80 p-4 dark:border-emerald-800 dark:bg-emerald-950/30">
                        <p className="text-xl font-black text-emerald-950 tamil-text dark:text-emerald-50" lang="ta">
                          {li + 1}. {line}
                        </p>
                      </div>
                      <input
                        type="text"
                        value={(multiAnswers[it.q_no] ?? [])[li] ?? ''}
                        onChange={(e) => {
                          mark();
                          setChecked(false);
                          setMultiAnswers((p) => {
                            const row = [...(p[it.q_no] ?? it.spokenLines.map(() => ''))];
                            row[li] = e.target.value;
                            return { ...p, [it.q_no]: row };
                          });
                        }}
                        className="w-full min-h-[52px] rounded-2xl border-4 border-teal-300 bg-teal-50/60 px-4 text-xl font-bold text-teal-950 outline-none focus:border-amber-400 dark:border-teal-700 dark:bg-slate-900 dark:text-white tamil-text"
                        lang="ta"
                        placeholder="எழுத்து வடிவம்…"
                      />
                    </div>
                  ))}
                </div>
              )}

              {checked && currentResult && !currentResult.skip ? (
                <div
                  className={clsx(
                    'mt-6 rounded-2xl border-4 p-4 text-center text-xl font-extrabold tamil-text',
                    currentResult.ok
                      ? 'border-emerald-400 bg-emerald-100 text-emerald-900 dark:border-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-50'
                      : 'border-amber-400 bg-amber-100 text-amber-950 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-50'
                  )}
                  lang="ta"
                >
                  {currentResult.ok ? '🌟 சரியானது!' : 'மீண்டும் முயற்சி செய்க'}
                </div>
              ) : null}
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              {hasAnswerKey && it && !it.incomplete ? (
                <button
                  type="button"
                  onClick={() => {
                    mark();
                    setChecked(true);
                  }}
                  className="min-h-[52px] rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 px-8 py-3 text-lg font-extrabold text-white shadow-xl tamil-text"
                  lang="ta"
                >
                  பதில் சரிபார்
                </button>
              ) : null}
              <button
                type="button"
                onClick={resetCurrent}
                className="min-h-[52px] rounded-2xl border-4 border-amber-400 bg-amber-100 px-6 py-3 text-lg font-extrabold text-amber-950 tamil-text dark:bg-amber-950/40 dark:text-amber-50"
                lang="ta"
              >
                மீண்டும் முயல்
              </button>
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
              <button
                type="button"
                disabled={step <= 0}
                onClick={() => {
                  mark();
                  setStep((s) => s - 1);
                  setChecked(false);
                }}
                className="inline-flex min-h-[52px] items-center gap-2 rounded-2xl border-4 border-emerald-300 bg-white px-4 py-3 font-extrabold text-emerald-900 disabled:opacity-40 dark:border-emerald-700 dark:bg-slate-800 dark:text-emerald-100 tamil-text"
                lang="ta"
              >
                <ChevronLeft className="h-6 w-6" />
                முந்தைய
              </button>
              <button
                type="button"
                disabled={step >= items.length - 1}
                onClick={() => {
                  mark();
                  setStep((s) => s + 1);
                  setChecked(false);
                }}
                className="inline-flex min-h-[52px] items-center gap-2 rounded-2xl border-4 border-emerald-300 bg-white px-4 py-3 font-extrabold text-emerald-900 disabled:opacity-40 dark:border-emerald-700 dark:bg-slate-800 dark:text-emerald-100 tamil-text"
                lang="ta"
              >
                அடுத்து
                <ChevronRight className="h-6 w-6" />
              </button>
            </div>

            <div className="mt-4 flex justify-center">
              <button
                type="button"
                onClick={resetAll}
                className="text-sm font-bold text-emerald-800 underline tamil-text dark:text-emerald-200"
                lang="ta"
              >
                எல்லாப் பதில்களையும் அழி
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};
